import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
    Button,
    Divider,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import DataGridCreateStrategy from "src/components/data-grid/activations/data-grid-create-strategy";
import {
    useAddActiveModeMutation,
    useAddKoBiddingAutoMaxCpcMutation,
    useAddKoBiddingManualMaxCpcMutation
} from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";

export default function ModalCreateStrategy({
    open,
    row,
    onClose,
    setLocalPatches
}: {
    open: boolean;
    row: any;
    onClose: () => void;
    setLocalPatches: any;
}) {
    const [updatedRow, setUpdatedRow] = useState<any[]>([]);

    const [addActiveMode] = useAddActiveModeMutation();
    const [addKoBiddingManualMaxCpc] = useAddKoBiddingManualMaxCpcMutation();
    const [addKoBiddingAutoMaxCpc] = useAddKoBiddingAutoMaxCpcMutation();

    const workspace = useCurrentWorkspace();
    const [errorCellId, setErrorCellId] = useState<string | null>(null);

    const [selectedMode, setSelectedMode]: any = useState("Active Mode");

    useEffect(() => {
        if (!row || row.length === 0) return;

        const sanitized = row.map((r: any) => {
            if (!r.device || r.device.length === 0) {
                return { ...r, device: ["desktop"] };
            }
            return r;
        });

        setUpdatedRow(sanitized);
    }, [row]);

    const handleSubmit = async () => {
        if (!workspace) return;

        try {
            // --- ACTIVE MODE ---
            if (selectedMode === "Active Mode") {

                const payload = updatedRow.map((item) => ({
                    ...item,
                    mode: "Active Mode",
                    campaign_type: "Search",
                    client: workspace?.bqTable,
                    workspaceId: workspace?.id,
                    domain: workspace?.url,
                    status: true,
                }));

                const result = await addActiveMode(payload).unwrap();
                // result = [{ id: 123, mode: "...", ... }, ...]

                enqueueSnackbar(`Active Mode added.`, { variant: "success" });

                // 🔥 Patch local avec les VRAIS IDs
                updatedRow.forEach((r, index) => {
                    const created = result[index];

                    setLocalPatches((prev: any) => ({
                        ...prev,
                        [r.id]: {
                            ...r,
                            activations_details: [
                                ...(r.activations_details || []),
                                {
                                    ...created,
                                    status: true
                                }
                            ]
                        }
                    }));
                });

                onClose();
                return;
            }

            // --- KO BIDDING MANUAL ---
            if (selectedMode === "KO Bidding Manual") {
                const payload = updatedRow.map((item) => {
                    const koDetail = item.activations_details?.find(
                        (a: any) => a.mode === "KO Bidding Manual"
                    );
                    const maxCpcValue = item.max_cpc ?? koDetail?.max_cpc;

                    return {
                        ...item,
                        max_cpc: maxCpcValue,
                        mode: "KO Bidding Manual",
                        match_type: 'EXACT',
                        client: workspace?.bqTable,
                        workspaceId: workspace?.id,
                        ad_group_names: item.ad_group_names[0],
                        ad_group_ids: item.ad_group_ids[0],
                        status: true,
                    };
                });

                const result = await addKoBiddingManualMaxCpc(payload).unwrap();

                enqueueSnackbar(`KO Manual added.`, { variant: "success" });

                updatedRow.forEach((r, index) => {
                    const created = result[index];

                    setLocalPatches((prev: any) => {
                        const previous = prev[r.id] ?? r;

                        const newDetails = [
                            ...(previous.activations_details?.filter(
                                (d: any) => d.mode !== "KO Bidding Manual"
                            ) ?? []),

                            {
                                ...created,
                                status: true,
                                mode: "KO Bidding Manual",
                                max_cpc: created.max_cpc ?? r.max_cpc,
                            },
                        ];

                        return {
                            ...prev,
                            [r.id]: {
                                ...previous,
                                activations_details: newDetails,
                            },
                        };
                    });
                });



                onClose();
                return;
            }

            // --- KO BIDDING AUTO ---
            if (selectedMode === "KO Bidding Auto") {
                const payload = updatedRow.map((item) => {
                    const detail = item.activations_details?.find(
                        (a: any) => a.mode && a.mode.startsWith("KO Bidding Auto")
                    );

                    return {
                        ...item,
                        client: workspace?.bqTable,
                        workspaceId: workspace?.id,
                        bidding_strategy_type: "Max CPC",
                        campaign_type: "Search",
                        domain: workspace?.url,
                        mode: "KO Bidding Auto - " + (detail?.strategy ?? "Smart Top"),
                        status: true,
                        initial_avg_cpc: item.avg_cpc,
                        initial_max_cpc: item.max_cpc,
                        accepted_variation_threshold: detail?.accepted_variation_threshold ?? 0,
                        strategy: detail?.strategy ?? "Smart Top",
                        ad_group_names: item.ad_group_names[0],
                        ad_group_ids: item.ad_group_ids[0],
                    };
                });

                const invalidRow = updatedRow.find((r) => {
                    const detail = r.activations_details?.find(
                        (d: any) => d.mode === "KO Bidding Auto"
                    );
                    return !detail?.accepted_variation_threshold || detail.accepted_variation_threshold < 1 || detail.accepted_variation_threshold > 99;
                });

                if (invalidRow) {
                    setErrorCellId(invalidRow.id);
                    enqueueSnackbar("Please fill in a valid Threshold (1-99) for all rows.", { variant: "error" });
                    return; // ❌ Stop submit
                }

                const result = await addKoBiddingAutoMaxCpc(payload).unwrap();

                enqueueSnackbar(`KO Auto added.`, { variant: "success" });

                updatedRow.forEach((r, index) => {
                    const created = result[index];

                    setLocalPatches((prev: any) => {
                        const prevRow = prev[r.id] ?? r;

                        return {
                            ...prev,
                            [r.id]: {
                                ...prevRow,
                                activations_details: [
                                    // ❗ On filtre l’ancien KO Auto pourri
                                    ...(prevRow.activations_details?.filter((d: any) =>
                                        !d.mode?.startsWith("KO Bidding Auto")
                                    ) ?? []),

                                    // On garde UNIQUEMENT l’objet renvoyé par le backend
                                    {
                                        ...created,
                                        status: true,
                                    }
                                ]
                            }
                        };
                    });
                });



                onClose();
                return;
            }

        } catch (err) {
            console.error("❌ Error:", err);
            enqueueSnackbar(`Failed to apply strategy`, { variant: "error" });
        }
    };


    const activations = Array.isArray(row) ? row[0]?.activations_details : null;

    const availableModes = ["Active Mode", "KO Bidding Manual", "KO Bidding Auto"].filter((mode) => {
        const hasModeActive = activations?.some(
            (a: any) => a.status === true && a.mode?.startsWith(mode)
        );

        if (hasModeActive) return false;

        const hasManual = activations?.some(
            (a: any) => a.mode === "KO Bidding Manual" && a.status === true
        );

        const hasAuto = activations?.some(
            (a: any) => a?.mode?.startsWith("KO Bidding Auto") && a.status === true
        );

        if (hasManual && mode === "KO Bidding Auto") return false;
        if (hasAuto && mode === "KO Bidding Manual") return false;

        return true;
    });

    useEffect(() => {
        if (!open || !row) return;

        setSelectedMode((prev: any) => {
            if (prev !== "Active Mode") return prev;

            if (!availableModes || availableModes.length === 0) {
                return null;
            }

            if (availableModes.includes("Active Mode")) {
                return "Active Mode";
            }

            return availableModes[0];
        });
    }, [open]);

    return (
        <Dialog
            open={open && !!row}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: "white",
                    borderRadius: 3,
                    py: 3,
                    color: "#FFF",
                    zIndex: 99999,
                },
            }}
        >
            <DialogTitle>
                <Typography color={'rgba(51, 60, 71, 1)'} fontWeight={600} fontSize={17}>
                    Add Bidding Strategy (Keyword Exact)
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Box mb={"16px"}>
                    <FormControl component="fieldset">
                        <Typography fontSize={"14px"} color={"rgba(99, 115, 129, 1)"}>Select Mode</Typography>

                        <RadioGroup
                            row
                            value={selectedMode ?? ""}
                            onChange={(e) => setSelectedMode(e.target.value)}
                            sx={{ display: "flex", gap: 2 }}
                        >
                            {availableModes.map((mode) => (
                                <FormControlLabel
                                    key={mode}
                                    value={mode}
                                    control={
                                        <Radio
                                            size="small"
                                            sx={{
                                                color: "rgba(145, 158, 171, 1)",
                                                "&.Mui-checked": {
                                                    color: "rgba(51, 60, 71, 1)",
                                                },
                                                "&:hover": {
                                                    backgroundColor: "rgba(200, 200, 200, 0.1)",
                                                },
                                            }}
                                        />
                                    }
                                    label={mode}
                                    sx={{
                                        color: selectedMode === mode ? "rgba(51, 60, 71, 1)" : "rgba(99, 115, 129, 1)",
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Box>

                <Divider sx={{ borderColor: "rgba(145, 158, 171, 0.20))" }} />

                <Box mt={"16px"}>
                    {open && row && (
                        <DataGridCreateStrategy
                            rows={updatedRow}
                            onChange={(newRow) => setUpdatedRow(newRow)}
                            mode={selectedMode}
                            errorCellId={errorCellId}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button sx={{
                    backgroundColor: "rgba(145, 158, 171, 0.12)", color: "rgba(145, 158, 171, 1)",
                    "&:hover": {
                        backgroundColor: "rgba(145, 158, 171, 0.24)",
                    },
                }} onClick={onClose} variant="contained" color="inherit">
                    Cancel
                </Button>
                <Button sx={{
                    backgroundColor: "rgba(24, 24, 24, 1)",
                    "&:hover": {
                        backgroundColor: "rgba(24, 24, 24, .9)",
                    },
                    color: 'white'
                }} onClick={handleSubmit} variant="contained">
                    Activate
                </Button>
            </DialogActions>
        </Dialog>
    );
}